import React, { useState, useEffect, useCallback, useMemo, useContext } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./Addticket.module.css";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useQuery, useMutation, useLazyQuery, useApolloClient } from '@apollo/client';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import {
  GET_ACTIVE_USERS,
  GET_ACTIVE_USERS_BY_SALES_REP,
  GET_LEAGUES,
  GET_EVENTS_BY_HOME_AND_AWAY_TEAM_ID,
  GET_EVENTS_BY_HOME_TEAM_ID,
  GET_EVENTS_BY_OTHER_LEAGUES,
  GET_EVENT_OTHER_LEAGUES,
  ADD_TICKETS_BY_ADMIN
} from '../AddTickets/api/addTicketsQuery';
import { GET_LEAGUE_CATEGORIES_BY_LEAGUE_ID } from '../AddTickets/api/addTicketsQuery';
import { globalContext } from "../../Provider/AppProvider";
import { roles } from "../../helper";
import { isEventEligibleForAddTickets } from "../../constants/addTicketEventVisibility";
import { GET_SALES_REPS_BY_EMAIL } from "../Salesrep/api/SalesrepQuery";
import { GET_ALL_AFFILIATES } from "../Users/api/UsersQuery";
import { GET_USERS_LIST } from "../Users/api/UsersQuery";
import { config } from "../../config/config";
import { GET_NOTIFICATIONS } from "../../components/shared-components/notification/api/NotificationQuery";
import { getNotificationQueryVariables, mergeNotificationQueryIntoGlobalState } from "../../utils/notification-query-variables";
// React Icons
import { FaUser, FaTag, FaCalendarAlt, FaInfoCircle } from 'react-icons/fa';
import { MdOutlineFolder } from 'react-icons/md';

// Types
type StepCompleted = {
  1: boolean;
  2: boolean;
  3: boolean;
  4: boolean;
  5?: boolean;
};

interface User {
  u_id: string;
  u_email_id: string;
  u_first_name: string;
  u_last_name: string;
  u_full_name: string;
  u_pricing_mode?: string | null;
  u_is_active?: boolean;
  u_delete_requested?: boolean;
  u_affiliate_id?: string | null;
}

interface League {
  l_id: string;
  l_name: string;
  l_type: string;
  l_order: number;
}

interface Team {
  lc_id: string;
  lc_name: string;
  lc_brand_name?: string;
  lc_market_name?: string;
}

interface Event {
  e_id: string;
  e_name: string;
  e_date: string;
  e_date_time_zone?: string;
  e_time_zone?: string;
  e_time?: string;
  e_address: string;
  e_stadium_name: string;
  e_status: string;
  e_is_active?: boolean;
  e_city: string;
  e_state: string;
}

// New Interface for Ticket Sets
interface TicketSet {
  id: string;
  section: string;
  row: string;
  startSeat: string;
  endSeat: string;
  listPrice: string;
}

const getEventDateTimeValue = (event: Event): string | undefined => {
  return event?.e_date_time_zone || event?.e_date || event?.e_time_zone || event?.e_time;
};

const formatEventDateTime = (event: Event): string => {
  const dateTimeValue = getEventDateTimeValue(event);
  if (!dateTimeValue) {
    return 'Date TBD';
  }

  const parsed = new Date(dateTimeValue);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toLocaleString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  }

  const normalized = String(dateTimeValue).trim().replace(' ', 'T');
  const parsedNormalized = new Date(normalized);
  if (!Number.isNaN(parsedNormalized.getTime())) {
    return parsedNormalized.toLocaleString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  }

  return dateTimeValue;
};

interface SalesRepLookupResponse {
  sales_reps: Array<{
    sr_id: string;
    sr_email: string;
  }>;
}

/** Concierge clients do not enter list price on add; staff sets price later. */
function isConciergePricingMode(pricingMode?: string | null): boolean {
  return (pricingMode ?? "").toString().trim().toLowerCase() === "concierge";
}

const AddTickets: React.FC = () => {
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const { globalState, handleGlobalState } = useContext<any>(globalContext);
  const userRole = globalState?.profile?.userRole || localStorage.getItem("x-hasura-user-role") || "";
  const isMarketingUser = false;
  const isAffiliateAdmin = userRole === roles.AFFILIATER_ADMIN;
  const loggedInEmail = (
    globalState?.profile?.mailId ||
    localStorage.getItem("adminMailId") ||
    ""
  ).toLowerCase().trim();

  // ==================== DARK MODE WITH PERSISTENCE ====================
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme === 'dark';
    }
    const dataTheme = document.documentElement.getAttribute('data-theme');
    if (dataTheme) {
      return dataTheme === 'dark';
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);
  
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      if (!localStorage.getItem('theme')) {
        setIsDarkMode(e.matches);
      }
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // ==================== STATE DECLARATIONS ====================
  
  // Dialog state
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
  const [isSubmittingFallback, setIsSubmittingFallback] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState<{open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning'}>({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // Step states
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [stepCompleted, setStepCompleted] = useState<StepCompleted>({
    1: false, 2: false, 3: false, 4: false, 5: false
  });

  // Selection states
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedLeague, setSelectedLeague] = useState<League | null>(null);
  const [selectedHomeTeam, setSelectedHomeTeam] = useState<Team | null>(null);
  const [selectedAwayTeam, setSelectedAwayTeam] = useState<Team | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [selectedBulkEvents, setSelectedBulkEvents] = useState<Event[]>([]);
  const [bulkEventPrices, setBulkEventPrices] = useState<Record<string, string>>({});

  // Search states
  const [searchUser, setSearchUser] = useState<string>("");
  const [searchHomeTeam, setSearchHomeTeam] = useState<string>("");
  const [searchAwayTeam, setSearchAwayTeam] = useState<string>("");
  const [searchEvent, setSearchEvent] = useState<string>("");

  // UI states
  const [showUserResults, setShowUserResults] = useState<boolean>(false);
  const [showHomeTeamResults, setShowHomeTeamResults] = useState<boolean>(false);
  const [showAwayTeamResults, setShowAwayTeamResults] = useState<boolean>(false);
  const [showEventResults, setShowEventResults] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isBulkSeasonSchedule, setIsBulkSeasonSchedule] = useState<boolean>(false);

  // ==================== TICKET SETS STATE ====================
  const [ticketSets, setTicketSets] = useState<TicketSet[]>([
    {
      id: `ticket-set-${Date.now()}-0`,
      section: "",
      row: "",
      startSeat: "",
      endSeat: "",
      listPrice: "",
    }
  ]);

  // ==================== TICKET SETS HANDLERS ====================

  const generateTicketSetId = () => {
    return `ticket-set-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };

  const handleTicketSetChange = (id: string, field: keyof TicketSet, value: string) => {
    setTicketSets(prev => 
      prev.map(set => 
        set.id === id ? { ...set, [field]: value } : set
      )
    );
  };

  const handleAddTicketSet = () => {
    setTicketSets(prev => [
      ...prev,
      {
        id: generateTicketSetId(),
        section: "",
        row: "",
        startSeat: "",
        endSeat: "",
        listPrice: "",
      }
    ]);
  };

  const handleRemoveTicketSet = (id: string) => {
    if (ticketSets.length <= 1) {
      setSnackbar({
        open: true,
        message: 'You need at least one ticket set.',
        severity: 'warning'
      });
      return;
    }
    setTicketSets(prev => prev.filter(set => set.id !== id));
  };

  // Validate all ticket sets
  const validateAllTicketSets = (): { valid: boolean; errors: string[] } => {
    const errors: string[] = [];
    
    ticketSets.forEach((set, index) => {
      const prefix = `Ticket Set ${index + 1}`;
      if (!set.section.trim()) {
        errors.push(`${prefix}: Please enter section`);
      }
      if (!set.row.trim()) {
        errors.push(`${prefix}: Please enter row`);
      }
      if (!set.startSeat.trim()) {
        errors.push(`${prefix}: Please enter start seat`);
      }
      if (set.endSeat.trim()) {
        const start = parseInt(set.startSeat);
        const end = parseInt(set.endSeat);
        if (!isNaN(start) && !isNaN(end) && end < start) {
          errors.push(`${prefix}: End seat must be greater than or equal to start seat`);
        }
      }
      if (listPriceRequired) {
        if (!isBulkSeasonSchedule && (!set.listPrice || parseFloat(set.listPrice) <= 0)) {
          errors.push(`${prefix}: Please enter a valid price`);
        }
      }
    });
    
    return { valid: errors.length === 0, errors };
  };

  const sanitizePriceInput = (value: string): string => {
    let sanitized = value.replace(/[^0-9.]/g, '');
    const firstDecimalIndex = sanitized.indexOf('.');
    if (firstDecimalIndex !== -1) {
      sanitized =
        sanitized.slice(0, firstDecimalIndex + 1) +
        sanitized.slice(firstDecimalIndex + 1).replace(/\./g, '');
    }
    return sanitized;
  };

  const extractMessageText = (message: unknown): string => {
    if (typeof message === 'string') return message.trim();
    if (message && typeof message === 'object') {
      const nested =
        (message as any).message ||
        (message as any).error ||
        (message as any).details ||
        (message as any).errors?.[0]?.message;
      if (typeof nested === 'string') return nested.trim();
    }
    return '';
  };

  const getPricingModeLabel = (pricingMode?: string | null) => {
    if (!pricingMode) return 'Self Pricing';

    const normalized = pricingMode.toString().trim().toLowerCase();
    if (normalized === 'self-pricing' || normalized === 'self pricing' || normalized === 'self') {
      return 'Self Pricing';
    }
    if (normalized === 'concierge') {
      return 'Concierge';
    }
    return pricingMode.toString();
  };

  const listPriceRequired = useMemo(
    () => (selectedUser ? !isConciergePricingMode(selectedUser.u_pricing_mode) : true),
    [selectedUser],
  );

  useEffect(() => {
    if (selectedUser && isConciergePricingMode(selectedUser.u_pricing_mode)) {
      setTicketSets(prev => 
        prev.map(set => ({ ...set, listPrice: "" }))
      );
    }
  }, [selectedUser?.u_id, selectedUser?.u_pricing_mode]);

  const getBulkEventPrice = (eventId: string) => (bulkEventPrices[eventId] || "");
  const getBulkEventPriceValue = (eventId: string) => parseFloat(getBulkEventPrice(eventId) || '0');
  const isBulkEventPriceValid = (eventId: string) => {
    if (!listPriceRequired) return true;
    const value = getBulkEventPrice(eventId).trim();
    return value !== '' && !Number.isNaN(Number(value)) && Number(value) > 0;
  };

  const showDuplicateAlert = (message: string) => {
    const normalizedMessage = (message || '').trim();
    setSnackbar({
      open: true,
      message: `❌ ${normalizedMessage || 'Ticket already exists for this section/row/seat.'}`,
      severity: 'error',
    });
  };

  const classifyAddTicketMessage = (message: unknown) => {
    const raw = extractMessageText(message);
    const normalized = raw.toLowerCase();
    const isDuplicateLike =
      normalized.includes("already added") ||
      normalized.includes("already exists") ||
      normalized.includes("duplicate") ||
      normalized.includes("no valid seats to list") ||
      normalized.includes("0 tickets were added newly") ||
      normalized.includes("0 ticket was added");

    if (isDuplicateLike) {
      return {
        shouldTreatAsError: true,
        displayMessage: raw || "Ticket already exists for this section/row/seat."
      };
    }

    return {
      shouldTreatAsError: false,
      displayMessage: raw || "Tickets added successfully!"
    };
  };

  // Pagination states
  const [userPage, setUserPage] = useState(0);
  const [teamPage, setTeamPage] = useState(0);
  const [eventPage, setEventPage] = useState(0);
  const pageSize = 10;

  // Debounce timeout for search
  const [homeTeamSearchTimeout, setHomeTeamSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [awayTeamSearchTimeout, setAwayTeamSearchTimeout] = useState<NodeJS.Timeout | null>(null);

  // ==================== DYNAMIC QUERIES ====================

  const { data: salesRepLookupData } = useQuery<SalesRepLookupResponse>(GET_SALES_REPS_BY_EMAIL, {
    skip: !isMarketingUser || !loggedInEmail,
    variables: { email: loggedInEmail },
    fetchPolicy: "network-only",
  });

  const scopedSalesRepId = salesRepLookupData?.sales_reps?.[0]?.sr_id || null;

  const { data: affiliatesData } = useQuery(GET_ALL_AFFILIATES, {
    skip: !isAffiliateAdmin,
    variables: {
      pageSize: 1000,
      pageNumber: 0,
      order_by: {},
      search_event: "%"
    },
    fetchPolicy: "network-only",
  });

  const fallbackAffiliateIdFromList = useMemo(() => {
    if (!isAffiliateAdmin || !loggedInEmail) return null;
    const list = affiliatesData?.affiliates || [];
    const match = list.find(
      (item: any) => (item?.a_email_id || "").toLowerCase().trim() === loggedInEmail
    );
    return match?.a_id || null;
  }, [affiliatesData, isAffiliateAdmin, loggedInEmail]);

  const scopedAffiliateId = fallbackAffiliateIdFromList || null;

  const {
    data: allUsersData,
    loading: allUsersLoading,
    error: allUsersError,
  } = useQuery(GET_ACTIVE_USERS, {
    skip: isMarketingUser || isAffiliateAdmin,
    variables: {
      pageSize,
      pageOffset: userPage * pageSize,
      searchText: searchUser ? `%${searchUser}%` : "%",
      order_by: [{ u_full_name: "asc" }]
    },
    fetchPolicy: 'network-only'
  });

  const {
    data: affiliateUsersData,
    loading: affiliateUsersLoading,
    error: affiliateUsersError,
  } = useQuery(GET_USERS_LIST, {
    skip: !isAffiliateAdmin,
    variables: {
      pageSize: 5000,
      pageOffset: 0,
      searchText: searchUser ? `%${searchUser}%` : "%",
      order_by: [{ u_full_name: "asc" }]
    },
    fetchPolicy: 'network-only'
  });

  const affiliateScopedUsers = useMemo(() => {
    const users = affiliateUsersData?.user || [];
    if (!isAffiliateAdmin || !scopedAffiliateId) return [];
    return users.filter(
      (user: User) =>
        user.u_affiliate_id === scopedAffiliateId &&
        user.u_is_active === true &&
        !user.u_delete_requested
    );
  }, [affiliateUsersData, isAffiliateAdmin, scopedAffiliateId]);

  const {
    data: scopedUsersData,
    loading: scopedUsersLoading,
    error: scopedUsersError,
  } = useQuery(GET_ACTIVE_USERS_BY_SALES_REP, {
    skip: !isMarketingUser || !scopedSalesRepId,
    variables: {
      salesRepId: scopedSalesRepId,
      pageSize: 5000,
      pageOffset: 0,
      searchText: searchUser ? `%${searchUser}%` : "%",
      order_by: [{ u_full_name: "asc" }]
    },
    fetchPolicy: 'network-only'
  });

  const usersData = isMarketingUser
    ? scopedUsersData
    : isAffiliateAdmin
      ? { user: affiliateScopedUsers }
      : allUsersData;
  const usersLoading = isMarketingUser
    ? scopedUsersLoading
    : isAffiliateAdmin
      ? affiliateUsersLoading
      : allUsersLoading;
  const usersError = isMarketingUser
    ? scopedUsersError
    : isAffiliateAdmin
      ? affiliateUsersError
      : allUsersError;

  // Get leagues
  const { 
    data: leaguesData, 
    loading: leaguesLoading,
    error: leaguesError,
    refetch: refetchLeagues
  } = useQuery(GET_LEAGUES, {
    fetchPolicy: 'network-only'
  });

  // Get teams by league
  const { 
    data: teamsData, 
    loading: teamsLoading,
    error: teamsError,
    refetch: refetchTeams
  } = useQuery(GET_LEAGUE_CATEGORIES_BY_LEAGUE_ID, {
    variables: {
      l_id: selectedLeague?.l_id,
      limit: pageSize,
      offset: teamPage * pageSize
    },
    skip: !selectedLeague || selectedLeague?.l_type !== 'Sports',
    fetchPolicy: 'network-only'
  });

  const [searchHomeTeams, { data: searchHomeTeamsData, loading: searchHomeTeamsLoading }] = useLazyQuery(GET_LEAGUE_CATEGORIES_BY_LEAGUE_ID, {
    fetchPolicy: 'network-only'
  });
  const [searchAwayTeams, { data: searchAwayTeamsData, loading: searchAwayTeamsLoading }] = useLazyQuery(GET_LEAGUE_CATEGORIES_BY_LEAGUE_ID, {
    fetchPolicy: 'network-only'
  });

  const { 
    data: sportsEventsData, 
    loading: sportsEventsLoading,
    error: sportsEventsError,
    refetch: refetchSportsEvents
  } = useQuery(GET_EVENTS_BY_HOME_AND_AWAY_TEAM_ID, {
    variables: {
      homeTeamId: selectedHomeTeam?.lc_id,
      awayTeamId: selectedAwayTeam?.lc_id,
      limit: pageSize,
      offset: eventPage * pageSize,
      orderBy: [{ e_date: "asc" }]
    },
    skip: !selectedHomeTeam || !selectedAwayTeam || selectedLeague?.l_type !== 'Sports',
    fetchPolicy: 'network-only'
  });
  const isSportsEventsAllowlistError = (sportsEventsError?.message || '').toLowerCase().includes('query is not allowed');

  const {
    data: bulkSportsEventsData,
    loading: bulkSportsEventsLoading,
    error: bulkSportsEventsError,
    refetch: refetchBulkSportsEvents
  } = useQuery(GET_EVENTS_BY_HOME_TEAM_ID, {
    variables: {
      homeTeamId: selectedHomeTeam?.lc_id,
      limit: pageSize,
      offset: eventPage * pageSize,
      orderBy: [{ e_date: "asc" }]
    },
    skip: !selectedHomeTeam || selectedLeague?.l_type !== 'Sports' || !isBulkSeasonSchedule,
    fetchPolicy: 'network-only'
  });
  const isBulkEventsAllowlistError = (bulkSportsEventsError?.message || '').toLowerCase().includes('query is not allowed');
  const {
    data: bulkFallbackEventsData,
    loading: bulkFallbackEventsLoading,
    error: bulkFallbackEventsError,
    refetch: refetchBulkFallbackEvents
  } = useQuery(GET_EVENTS_BY_OTHER_LEAGUES, {
    variables: {
      leagueId: selectedLeague?.l_id,
      eventName: selectedHomeTeam?.lc_name ? `%${selectedHomeTeam.lc_name}%` : "%",
      limit: pageSize,
      offset: eventPage * pageSize,
      orderBy: [{ e_date: "asc" }]
    },
    skip: !selectedLeague || selectedLeague?.l_type !== 'Sports' || !isBulkSeasonSchedule || !selectedHomeTeam || !isBulkEventsAllowlistError,
    fetchPolicy: 'network-only'
  });
  const { 
    data: otherEventsData, 
    loading: otherEventsLoading,
    error: otherEventsError,
    refetch: refetchOtherEvents
  } = useQuery(GET_EVENTS_BY_OTHER_LEAGUES, {
    variables: {
      leagueId: selectedLeague?.l_id,
      eventName: searchEvent ? `%${searchEvent}%` : "%",
      limit: pageSize,
      offset: eventPage * pageSize,
      orderBy: [{ e_date: "asc" }]
    },
    skip: !selectedLeague || selectedLeague?.l_type === 'Sports',
    fetchPolicy: 'network-only'
  });
  const isOtherEventsAllowlistError = (otherEventsError?.message || '').toLowerCase().includes('query is not allowed');

  const legacyFallbackSearchString = useMemo(() => {
    if (selectedLeague?.l_type === 'Sports') {
      if (isBulkSeasonSchedule) {
        return selectedHomeTeam?.lc_name ? `%${selectedHomeTeam.lc_name}%` : '%';
      }
      return selectedHomeTeam?.lc_name ? `%${selectedHomeTeam.lc_name}%` : '%';
    }
    return searchEvent ? `%${searchEvent}%` : '%';
  }, [selectedLeague?.l_type, isBulkSeasonSchedule, selectedHomeTeam?.lc_name, searchEvent]);

  const shouldUseLegacyEventsFallback =
    isBulkEventsAllowlistError || isSportsEventsAllowlistError || isOtherEventsAllowlistError;

  const {
    data: legacyFallbackEventsData,
    loading: legacyFallbackEventsLoading,
    error: legacyFallbackEventsError,
    refetch: refetchLegacyFallbackEvents
  } = useQuery(GET_EVENT_OTHER_LEAGUES, {
    variables: {
      l_id: selectedLeague?.l_id,
      search_string: legacyFallbackSearchString,
      limit: pageSize,
      offset: eventPage * pageSize
    },
    skip: !selectedLeague?.l_id || !shouldUseLegacyEventsFallback,
    fetchPolicy: 'network-only'
  });

  const refreshDashboardNotifications = async () => {
    const adminId = globalState?.profile?.id;
    if (!adminId) return;
    try {
      const { data } = await apolloClient.query({
        query: GET_NOTIFICATIONS,
        variables: getNotificationQueryVariables(adminId),
        fetchPolicy: 'network-only',
      });
      handleGlobalState((prev: typeof globalState) =>
        mergeNotificationQueryIntoGlobalState(prev, data),
      );
    } catch {
      // Non-blocking: listing still succeeded
    }
  };

  // ==================== MUTATIONS ====================

  const [addTickets, { loading: addTicketsLoading }] = useMutation(ADD_TICKETS_BY_ADMIN, {
    onCompleted: async (data) => {
    
      
      setShowConfirmDialog(false);
      
      const apiMessage = data?.addTicketsByAdmin?.message;
      const outcome = classifyAddTicketMessage(apiMessage);

      if (outcome.shouldTreatAsError) {
        showDuplicateAlert(outcome.displayMessage);
        return;
      }
      
      
      
      setSnackbar({
        open: true,
        message: `✅ ${outcome.displayMessage}`,
        severity: 'success'
      });
      markManageTicketsBulkProcessingWindow(apiMessage);
      await refreshDashboardNotifications();
      resetManageTicketFilters();
      resetForm();
      navigate('/managetickets');
    },
  onError: (error) => {
  console.error('[ADD_TICKETS] Full error object:', error);
  setShowConfirmDialog(false);

  // 1. Raw message తీసుకోండి
  const rawMessage = error.graphQLErrors?.[0]?.message || error.message || 'Failed to add tickets';
  // 2. JSON/object ని Clean చేయండి (ఇది మీ దగ్గర ఇప్పటికే ఉన్న function)
  const cleanMessage = extractMessageText(rawMessage);
  const normalized = cleanMessage.toLowerCase();

  // 3. Duplicate error కోసం Check చేయండి
  if (
    normalized.includes('already added') ||
    normalized.includes('already exists') ||
    normalized.includes('duplicate') ||
    normalized.includes('no valid seats') ||
    normalized.includes('0 tickets were added')
  ) {
    showDuplicateAlert(cleanMessage || 'Ticket already exists for this section/row/seat.');
    return;
  }

  // 4. మిగిలిన Errors కి Generic Message
  setSnackbar({
    open: true,
    message: `❌ Error: ${cleanMessage}`,
    severity: 'error'
  });
}
  });
  const isSubmitting = addTicketsLoading || isSubmittingFallback;

  const callAddTicketsByAdminRest = async (payload: any) => {
    const nodeApiBaseUrl = (process.env.REACT_APP_NODE_API_URL || config.node_api_url || '').replace(/\/+$/, '');
    if (!nodeApiBaseUrl) {
      throw new Error('API base URL is not configured. Please contact support.');
    }
    const idTokenKey = Object.keys(localStorage).find((key) => key.includes('idToken')) as string;
    const idToken = idTokenKey ? localStorage.getItem(idTokenKey) : '';
    const hasuraRole = localStorage.getItem('x-hasura-user-role');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (idToken) headers.Authorization = `Bearer ${idToken}`;
    if (hasuraRole) headers['x-hasura-role'] = hasuraRole;
    const response = await fetch(`${nodeApiBaseUrl}/api/addTicketsBySuperUser`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ input: { addTicketsByAdminInput: payload } })
    });
    const responseData = await response.json().catch(() => ({}));
    if (!response.ok || responseData?.success === false) {
      const rawMessage = responseData?.message;
      const extractedMessage = typeof rawMessage === 'string' ? rawMessage : rawMessage?.message || responseData?.error || responseData?.errors?.[0]?.message || 'Failed to add tickets';
      const err: any = new Error(extractedMessage);
      err.status = response.status;
      err.responseData = responseData;
      throw err;
    }
    return responseData;
  };
  
  const resetManageTicketFilters = () => {
    const tempState = { ...globalState };
    tempState.globalSearch.searchtext = "";
    tempState.notification.notificationClicked = false;
    tempState.notification.ticketId = null;
    tempState.notification.tpId = null;
    tempState.notification.tp_id_array = null;
    tempState.error.errorClicked = false;
    tempState.error.approximateTpArray = null;
    tempState.error.ticketPlacementId = null;
    tempState.error.text = "";
    tempState.manageTicketFilter = {
      ...tempState.manageTicketFilter,
      leagueId: null,
      leagueName: '',
      validstatus: null,
      startdate: null,
      enddate: null,
      periodLeft: null,
      selectedDateValue: '',
      isCustomRange: false,
      startDateRange: null,
      endDateRange: null,
      isFilter: false,
      clicked: false
    };
    handleGlobalState(tempState);
  };

  const markManageTicketsBulkProcessingWindow = (apiMessage?: string) => {
    if (typeof window === 'undefined') return;
    const msg = (apiMessage || '').toLowerCase();
    if (!msg.includes('currently being processed')) return;
    const expiresAt = Date.now() + (3 * 60 * 1000);
    sessionStorage.setItem('manageTicketsBulkProcessingUntil', String(expiresAt));
  };

  // ==================== REAL EVENTS DATA ====================
  
  const bulkFallbackEvents = (bulkFallbackEventsData?.events || []).filter((event: Event) =>
    selectedHomeTeam?.lc_name
      ? event.e_name?.toLowerCase().includes(selectedHomeTeam.lc_name.toLowerCase())
      : true
  );
  const legacyFallbackEventsRaw = (legacyFallbackEventsData?.events || []).map((event: any) => ({
    e_id: event.e_id,
    e_name: event.e_name,
    e_date: event.e_date || event.e_date_time_zone,
    e_address: event.e_brand_name || '',
    e_status: event.e_status,
    e_is_active: event.e_is_active,
    e_state: event.e_state || '',
    e_city: '',
    e_stadium_name: event.e_brand_name || '',
    e_country: '',
    e_game_id: '',
    e_cdt_date: event.e_date || event.e_date_time_zone || '',
    e_cdt_date_with_year: event.e_date || event.e_date_time_zone || ''
  }));
  const legacyFallbackEvents = selectedLeague?.l_type === 'Sports' && !isBulkSeasonSchedule && selectedAwayTeam?.lc_name
    ? legacyFallbackEventsRaw.filter((event: Event) =>
        event.e_name?.toLowerCase().includes(selectedAwayTeam.lc_name.toLowerCase())
      )
    : legacyFallbackEventsRaw;
  const realEvents = selectedLeague?.l_type === 'Sports'
    ? (isBulkSeasonSchedule
      ? (isBulkEventsAllowlistError
        ? (bulkFallbackEvents.length > 0 ? bulkFallbackEvents : legacyFallbackEvents)
        : (bulkSportsEventsData?.events || []))
      : (isSportsEventsAllowlistError ? legacyFallbackEvents : (sportsEventsData?.events || [])))
    : (isOtherEventsAllowlistError ? legacyFallbackEvents : (otherEventsData?.events || []));

  const filteredEvents = realEvents.filter((event: Event) => {
    if (!isEventEligibleForAddTickets(event)) return false;
    if (!searchEvent) return true;
    const query = searchEvent.toLowerCase();
    return (
      event.e_name.toLowerCase().includes(query) ||
      (event.e_stadium_name && event.e_stadium_name.toLowerCase().includes(query)) ||
      (event.e_city && event.e_city.toLowerCase().includes(query))
    );
  });

  const eventsLoading = selectedLeague?.l_type === 'Sports'
    ? (isBulkSeasonSchedule
      ? (isBulkEventsAllowlistError
        ? (bulkFallbackEventsLoading || legacyFallbackEventsLoading)
        : bulkSportsEventsLoading)
      : (isSportsEventsAllowlistError ? legacyFallbackEventsLoading : sportsEventsLoading))
    : (isOtherEventsAllowlistError ? legacyFallbackEventsLoading : otherEventsLoading);
  const eventsError = selectedLeague?.l_type === 'Sports'
    ? (isBulkSeasonSchedule
      ? (isBulkEventsAllowlistError
        ? (bulkFallbackEventsError || legacyFallbackEventsError)
        : bulkSportsEventsError)
      : (isSportsEventsAllowlistError ? legacyFallbackEventsError : sportsEventsError))
    : (isOtherEventsAllowlistError ? legacyFallbackEventsError : otherEventsError);

  // Search results for teams
  const homeTeamSearchResults = useMemo(() => {
    if (!searchHomeTeam || searchHomeTeam.length < 2) return [];
    return searchHomeTeamsData?.league_categories || [];
  }, [searchHomeTeamsData, searchHomeTeam]);

  const awayTeamSearchResults = useMemo(() => {
    if (!searchAwayTeam || searchAwayTeam.length < 2) return [];
    return searchAwayTeamsData?.league_categories || [];
  }, [searchAwayTeamsData, searchAwayTeam]);

  // ==================== EFFECTS ====================

  useEffect(() => {
    if (selectedLeague?.l_type === 'Sports' && !isBulkSeasonSchedule && selectedHomeTeam && selectedAwayTeam) {
      setEventPage(0);
      setShowEventResults(true);
      setSelectedEvent(null);
      refetchSportsEvents();
    }
  }, [selectedHomeTeam, selectedAwayTeam, selectedLeague, isBulkSeasonSchedule]);

  useEffect(() => {
    if (selectedLeague?.l_type === 'Sports' && isBulkSeasonSchedule && selectedHomeTeam) {
      setEventPage(0);
      setShowEventResults(true);
      setSelectedEvent(null);
      setSelectedBulkEvents([]);
      refetchBulkSportsEvents();
      if (isBulkEventsAllowlistError) {
        refetchBulkFallbackEvents();
      }
      if (shouldUseLegacyEventsFallback) {
        refetchLegacyFallbackEvents();
      }
    }
  }, [selectedHomeTeam, selectedLeague, isBulkSeasonSchedule, isBulkEventsAllowlistError, shouldUseLegacyEventsFallback]);

  useEffect(() => {
    if (isBulkSeasonSchedule && currentStep === 4) {
      setCurrentStep(5);
    }
  }, [isBulkSeasonSchedule, currentStep]);

  useEffect(() => {
    if (selectedLeague && selectedLeague?.l_type !== 'Sports') {
      setEventPage(0);
      setSearchEvent("");
      setShowEventResults(true);
      setSelectedEvent(null);
      refetchOtherEvents();
    }
  }, [selectedLeague]);

  // Debounced search for home team
  useEffect(() => {
    if (homeTeamSearchTimeout) clearTimeout(homeTeamSearchTimeout);

    if (searchHomeTeam && searchHomeTeam.length >= 2 && selectedLeague?.l_id) {
      const timeout = setTimeout(() => {
        searchHomeTeams({
          variables: {
            l_id: selectedLeague.l_id,
            lcName: `%${searchHomeTeam}%`,
            limit: 10
          }
        });
      }, 300);
      setHomeTeamSearchTimeout(timeout);
    }

    return () => {
      if (homeTeamSearchTimeout) clearTimeout(homeTeamSearchTimeout);
    };
  }, [searchHomeTeam, selectedLeague?.l_id]);

  // Debounced search for away team
  useEffect(() => {
    if (awayTeamSearchTimeout) clearTimeout(awayTeamSearchTimeout);

    if (searchAwayTeam && searchAwayTeam.length >= 2 && selectedLeague?.l_id) {
      const timeout = setTimeout(() => {
        searchAwayTeams({
          variables: {
            l_id: selectedLeague.l_id,
            lcName: `%${searchAwayTeam}%`,
            limit: 10
          }
        });
      }, 300);
      setAwayTeamSearchTimeout(timeout);
    }

    return () => {
      if (awayTeamSearchTimeout) clearTimeout(awayTeamSearchTimeout);
    };
  }, [searchAwayTeam, selectedLeague?.l_id]);

  // ==================== HANDLERS ====================

  const resetForm = () => {
    setSelectedUser(null);
    setSelectedLeague(null);
    setSelectedHomeTeam(null);
    setSelectedAwayTeam(null);
    setSelectedEvent(null);
    setSelectedBulkEvents([]);
    setBulkEventPrices({});
    setTicketSets([
      {
        id: `ticket-set-${Date.now()}-0`,
        section: "",
        row: "",
        startSeat: "",
        endSeat: "",
        listPrice: "",
      }
    ]);
    setCurrentStep(1);
    setStepCompleted({ 1: false, 2: false, 3: false, 4: false, 5: false });
    setSearchUser("");
    setSearchHomeTeam("");
    setSearchAwayTeam("");
    setSearchEvent("");
    setShowEventResults(false);
    setIsBulkSeasonSchedule(false);
  };

  // Modified validateTicketDetails to use the new validation
  const validateTicketDetails = (): boolean => {
    // Validate user selection
    if (!selectedUser) {
      setSnackbar({ open: true, message: 'Please select a user', severity: 'error' });
      return false;
    }
    if (!selectedLeague) {
      setSnackbar({ open: true, message: 'Please select a league', severity: 'error' });
      return false;
    }
    
    const hasSelectedEvent = isBulkSeasonSchedule ? selectedBulkEvents.length > 0 : Boolean(selectedEvent);
    if (!hasSelectedEvent) {
      setSnackbar({ 
        open: true, 
        message: isBulkSeasonSchedule ? 'Please select at least one event' : 'Please select an event', 
        severity: 'error' 
      });
      return false;
    }
    
    // Validate ticket sets
    const validation = validateAllTicketSets();
    if (!validation.valid) {
      setSnackbar({ 
        open: true, 
        message: validation.errors.join('. '), 
        severity: 'error' 
      });
      return false;
    }
    
    return true;
  };

  // Modified prepareSeatNumbers to accept parameters
  const prepareSeatNumbers = (startSeatStr: string, endSeatStr: string): string[] => {
    const start = Number(startSeatStr.trim());
    if (!Number.isInteger(start) || start <= 0) {
      throw new Error('Start seat must be a positive integer');
    }

    if (!endSeatStr.trim()) {
      return [String(start)];
    }

    const end = Number(endSeatStr.trim());
    if (!Number.isInteger(end) || end <= 0) {
      throw new Error('End seat must be a positive integer');
    }
    if (end < start) {
      throw new Error('Invalid seat range. End seat must be greater than or equal to start seat.');
    }

    const seats: string[] = [];
    for (let i = start; i <= end; i++) {
      seats.push(String(i));
    }
    return seats;
  };

  // Modified prepareTicketData to handle multiple ticket sets
  const prepareTicketData = () => {
    const targetEvents = isBulkSeasonSchedule
      ? selectedBulkEvents
      : (selectedEvent ? [selectedEvent] : []);

    const buildTicketRowForEvent = (event: Event, ticketSet: TicketSet) => {
      const seatNumbers = prepareSeatNumbers(ticketSet.startSeat, ticketSet.endSeat);
      const ticketRow: Record<string, unknown> = {
        section: ticketSet.section.trim(),
        row: ticketSet.row.trim(),
        seatNo: seatNumbers,
      };

      const eventPrice = isBulkSeasonSchedule
        ? getBulkEventPriceValue(event.e_id)
        : parseFloat(ticketSet.listPrice);

      if (listPriceRequired && Number.isFinite(eventPrice) && eventPrice > 0) {
        ticketRow.listPrice = eventPrice;
        ticketRow.tp_list_price = eventPrice;
        ticketRow.list_price = eventPrice;
      }

      return ticketRow;
    };

    // Flatten all ticket sets into ticket details
    const ticketDetails = targetEvents.map((event) => {
      const tickets = ticketSets.map(set => buildTicketRowForEvent(event, set));
      return {
        eventId: event.e_id,
        tickets: tickets
      };
    });

    const ticketInput = {
      addTicketsByAdminInput: {
        userId: selectedUser!.u_id,
        isBulkList: isBulkSeasonSchedule,
        ticketDetails: ticketDetails
      }
    };
    
    console.log('[TICKET_PREP] Final ticket structure with multiple sets:', JSON.stringify(ticketInput, null, 2));
    return { ticketInput };
  };

  // ========== MODIFIED HANDLER: SHOW CONFIRMATION DIALOG ==========
  const handleTransferTicketClick = () => {
    if (!validateTicketDetails()) return;
    
    // Validate seat ranges for all ticket sets
    for (const set of ticketSets) {
      if (set.startSeat && set.endSeat) {
        const start = parseInt(set.startSeat);
        const end = parseInt(set.endSeat);
        if (isNaN(start) || isNaN(end) || start > end) {
          setSnackbar({ open: true, message: 'Invalid seat range in one of the ticket sets', severity: 'error' });
          return;
        }
      }
    }
    setShowConfirmDialog(true);
  };

  const handleConfirmAdd = async () => {
    // Defensive: check all required fields before preparing data
    const hasSelectedEvent = isBulkSeasonSchedule ? selectedBulkEvents.length > 0 : Boolean(selectedEvent);
    const validation = validateAllTicketSets();
    if (!selectedUser || !hasSelectedEvent || !validation.valid) {
      setSnackbar({
        open: true,
        message: 'Please fill all required fields and select an event before adding tickets.',
        severity: 'error'
      });
      return;
    }
    if ((isMarketingUser || isAffiliateAdmin) && !allowedUserIds.has(selectedUser.u_id)) {
      setSnackbar({
        open: true,
        message: isAffiliateAdmin
          ? 'You can only add tickets for your affiliate users.'
          : 'You can only add tickets for your assigned users.',
        severity: 'error'
      });
      return;
    }
    let ticketInput: any;
    try {
      const prepared = prepareTicketData();
      ticketInput = prepared.ticketInput;
      console.log('[ADD_TICKETS] Prepared ticket input:', JSON.stringify(ticketInput, null, 2));
    } catch (error: any) {
      console.error('[ADD_TICKETS] Error preparing data:', error);
      setSnackbar({
        open: true,
        message: error?.message || 'Invalid seat details',
        severity: 'error'
      });
      return;
    }
    if (!ticketInput.addTicketsByAdminInput || !ticketInput.addTicketsByAdminInput.userId || !ticketInput.addTicketsByAdminInput.ticketDetails?.length) {
      setSnackbar({
        open: true,
        message: 'Ticket input is missing required user or event information.',
        severity: 'error'
      });
      return;
    }

    try {
      setIsSubmittingFallback(true);
      const adminInput = ticketInput.addTicketsByAdminInput;

      if (adminInput?.isBulkList && Array.isArray(adminInput.ticketDetails) && adminInput.ticketDetails.length > 0) {
        let successCount = 0;
        const errorMessages: string[] = [];

        for (const ticketDetail of adminInput.ticketDetails) {
          try {
            const singleResp = await callAddTicketsByAdminRest({
              userId: adminInput.userId,
              isBulkList: false,
              ticketDetails: [ticketDetail],
            });
            const singleOutcome = classifyAddTicketMessage(singleResp?.message);
            if (singleOutcome.shouldTreatAsError) {
              errorMessages.push(singleOutcome.displayMessage);
            } else {
              successCount += 1;
            }
          } catch (singleErr: any) {
            const errMsg = extractMessageText(
              singleErr?.message ||
              singleErr?.responseData?.message ||
              singleErr?.responseData?.error
            );
            errorMessages.push(errMsg || 'Failed to add tickets for one event.');
          }
        }

        setShowConfirmDialog(false);

        if (successCount === 0) {
          setSnackbar({
            open: true,
            message: `❌ Error: ${errorMessages[0] || 'Unable to add tickets.'}`,
            severity: 'error'
          });
          return;
        }

        setSnackbar({
          open: true,
          message: errorMessages.length
            ? `✅ Added tickets for ${successCount} event(s). ${errorMessages.length} event(s) failed.`
            : `✅ Added tickets for ${successCount} event(s) successfully.`,
          severity: 'success'
        });
        resetManageTicketFilters();
        resetForm();
        navigate('/managetickets');
        return;
      }

      const restResp = await callAddTicketsByAdminRest(adminInput);
      setShowConfirmDialog(false);
      const outcome = classifyAddTicketMessage(restResp?.message);
      if (outcome.shouldTreatAsError) {
        setSnackbar({
          open: true,
          message: `❌ Error: ${outcome.displayMessage}`,
          severity: 'error'
        });
        return;
      }
      setSnackbar({
        open: true,
        message: `✅ ${outcome.displayMessage}`,
        severity: 'success'
      });
      markManageTicketsBulkProcessingWindow(restResp?.message);
      resetManageTicketFilters();
      resetForm();
      navigate('/managetickets');
      return;
    } catch (restError: any) {
      console.error('[ADD_TICKETS] REST add failed, falling back to GraphQL action:', restError);
      const restMessage = restError?.message || restError?.responseData?.message || restError?.responseData?.error || '';
      const restOutcome = classifyAddTicketMessage(restMessage);
      setShowConfirmDialog(false);

      if (ticketInput) {
        try {
          setSnackbar({
            open: true,
            message: 'Unable to reach ticket service. Retrying with GraphQL...',
            severity: 'info'
          });
          await addTickets({ variables: ticketInput });
          return;
        } catch (graphqlError: any) {
  console.error('[ADD_TICKETS] GraphQL fallback also failed:', graphqlError);
  const rawGraphql = graphqlError?.message || 'Failed to add tickets via GraphQL fallback';
  const cleanGraphql = extractMessageText(rawGraphql);  

  const norm = cleanGraphql.toLowerCase();
  if (
    norm.includes('already added') ||
    norm.includes('already exists') ||
    norm.includes('duplicate') ||
    norm.includes('no valid seats') ||
    norm.includes('0 tickets were added')
  ) {
    showDuplicateAlert(cleanGraphql);  
  } else {
    setSnackbar({
      open: true,
      message: `❌ Error: ${cleanGraphql}`,
      severity: 'error'
    });
  }
  return;
}
      }

      if (restOutcome.shouldTreatAsError) {
        showDuplicateAlert(restOutcome.displayMessage);
      } else {
        setSnackbar({
          open: true,
          message: `❌ Error: ${restOutcome.displayMessage || 'Failed to add tickets'}`,
          severity: 'error'
        });
      }
      return;
    } finally {
      setIsSubmittingFallback(false);
    }
  };

  const handleCancel = () => {
    setShowConfirmDialog(false);
  };

  const handleUserSelect = (user: User) => {
    setSelectedUser(user);
    setShowUserResults(false);
    setSearchUser(user.u_full_name || `${user.u_first_name} ${user.u_last_name}`);
    setStepCompleted(prev => ({ ...prev, 1: true }));
    setTimeout(() => setCurrentStep(2), 300);
  };

  const handleLeagueSelect = (league: League) => {
    setSelectedLeague(league);
    setStepCompleted(prev => ({ ...prev, 2: true }));
    
    setSelectedHomeTeam(null);
    setSelectedAwayTeam(null);
    setSelectedEvent(null);
    setSelectedBulkEvents([]);
    setSearchHomeTeam("");
    setSearchAwayTeam("");
    setSearchEvent("");
    setShowEventResults(false);
    
    if (league.l_type === 'Sports') {
      setTimeout(() => setCurrentStep(3), 300);
    } else {
      setShowEventResults(true);
      setTimeout(() => setCurrentStep(3), 300);
    }
  };

  const handleHomeTeamSelect = (team: Team) => {
    if (selectedAwayTeam && selectedAwayTeam.lc_id === team.lc_id) {
      setSnackbar({
        open: true,
        message: 'Home and Away teams must be different.',
        severity: 'error'
      });
      return;
    }
    setSelectedHomeTeam(team);
    setSelectedBulkEvents([]);
    setBulkEventPrices({});
    if (isBulkSeasonSchedule) {
      setSelectedAwayTeam(null);
      setSearchAwayTeam("");
      setStepCompleted(prev => ({ ...prev, 3: true, 4: true }));
      setTimeout(() => setCurrentStep(5), 300);
      return;
    }
    setShowHomeTeamResults(false);
    setSearchHomeTeam(team.lc_name);
    setStepCompleted(prev => ({ ...prev, 3: true }));
    setTimeout(() => setCurrentStep(4), 300);
  };

  const handleAwayTeamSelect = (team: Team) => {
    if (selectedHomeTeam && selectedHomeTeam.lc_id === team.lc_id) {
      setSnackbar({
        open: true,
        message: 'Home and Away teams must be different.',
        severity: 'error'
      });
      return;
    }
    setSelectedAwayTeam(team);
    setSelectedBulkEvents([]);
    setBulkEventPrices({});
    setShowAwayTeamResults(false);
    setSearchAwayTeam(team.lc_name);
    setStepCompleted(prev => ({ ...prev, 4: true }));
    setTimeout(() => setCurrentStep(5), 300);
  };

  const handleEventSelect = (event: Event) => {
    if (isBulkSeasonSchedule) {
      setSelectedBulkEvents((prev) => {
        const exists = prev.some((item) => item.e_id === event.e_id);
        const next = exists ? prev.filter((item) => item.e_id !== event.e_id) : [...prev, event];
        setStepCompleted((prevSteps) => ({ ...prevSteps, 5: next.length > 0 }));

        setBulkEventPrices((prevPrices) => {
          if (exists) {
            const { [event.e_id]: _, ...rest } = prevPrices;
            return rest;
          }
          // Use the first ticket set's price as default for bulk
          const defaultPrice = ticketSets[0]?.listPrice || '';
          return {
            ...prevPrices,
            [event.e_id]: defaultPrice
          };
        });

        return next;
      });
      return;
    }
    setSelectedEvent(event);
    setShowEventResults(false);
    setSearchEvent(event.e_name);
    
    if (selectedLeague?.l_type === 'Sports') {
      setStepCompleted(prev => ({ ...prev, 5: true }));
      setTimeout(() => setCurrentStep(5), 300);
    } else {
      setStepCompleted(prev => ({ ...prev, 3: true }));
      setTimeout(() => setCurrentStep(4), 300);
    }
  };

  const handleStepClick = (step: number) => {
    if (selectedLeague?.l_type === 'Sports') {
      if (step === 1) setCurrentStep(1);
      else if (step === 2 && stepCompleted[1]) setCurrentStep(2);
      else if (step === 3 && stepCompleted[2]) setCurrentStep(3);
      else if (step === 4 && stepCompleted[3] && !isBulkSeasonSchedule) setCurrentStep(4);
      else if (step === 5 && (stepCompleted[4] || isBulkSeasonSchedule)) setCurrentStep(5);
    } else {
      if (step === 1) setCurrentStep(1);
      else if (step === 2 && stepCompleted[1]) setCurrentStep(2);
      else if (step === 3 && stepCompleted[2]) setCurrentStep(3);
      else if (step === 4 && stepCompleted[3]) setCurrentStep(4);
    }
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleUserSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchUser(value);
    setShowUserResults(value.length > 0);
    setUserPage(0);
  };

  const handleHomeTeamSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchHomeTeam(value);
    setShowHomeTeamResults(value.length >= 2);
  };

  const handleAwayTeamSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchAwayTeam(value);
    setShowAwayTeamResults(value.length >= 2);
  };

  const handleEventSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchEvent(value);
    setShowEventResults(true);
    setEventPage(0);
    
    if (selectedLeague?.l_type === 'Sports') {
      if (isBulkSeasonSchedule) {
        refetchBulkSportsEvents();
      } else {
        refetchSportsEvents();
      }
    } else {
      refetchOtherEvents();
    }
  };

  // Get data
  const filteredUsers = (usersData?.user || []).filter((user: User) => {
    return user.u_is_active !== false && !user.u_delete_requested && (user as any).u_deleted_at == null;
  });
  const allowedUserIds = useMemo(() => new Set(filteredUsers.map((user: User) => user.u_id)), [filteredUsers]);
  const filteredTeams = teamsData?.league_categories || [];

  const getTotalPrice = () => {
    let total = 0;
    ticketSets.forEach(set => {
      let seatCount = 1;
      if (set.startSeat && set.endSeat) {
        const start = parseInt(set.startSeat);
        const end = parseInt(set.endSeat);
        if (!isNaN(start) && !isNaN(end) && start <= end) {
          seatCount = end - start + 1;
        }
      }
      total += (parseFloat(set.listPrice) || 0) * seatCount;
    });
    return total;
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // ==================== RENDER TICKET SETS FORM ====================
  const renderTicketSetsForm = () => {
    return (
      <div className={styles.ticketSetsContainer}>
        {ticketSets.map((set, index) => (
          <div key={set.id} className={styles.ticketSetCard}>
            <div className={styles.ticketSetHeader}>
              <h4 className={styles.ticketSetTitle}>Ticket Set {index + 1}</h4>
              {ticketSets.length > 1 && (
                <button
                  className={styles.removeTicketSetBtn}
                  onClick={() => handleRemoveTicketSet(set.id)}
                  type="button"
                >
                  <DeleteIcon fontSize="small" />
                  Remove
                </button>
              )}
            </div>
            
            <div className={styles.ticketSetForm}>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Section *</label>
                  <input
                    type="text"
                    placeholder="e.g., 120"
                    className={styles.formInput}
                    value={set.section}
                    onChange={(e) => handleTicketSetChange(set.id, 'section', e.target.value)}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Row *</label>
                  <input
                    type="text"
                    placeholder="e.g., 7"
                    className={styles.formInput}
                    value={set.row}
                    onChange={(e) => handleTicketSetChange(set.id, 'row', e.target.value)}
                  />
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Start Seat *</label>
                  <input
                    type="text"
                    placeholder="e.g., 1"
                    className={styles.formInput}
                    value={set.startSeat}
                    onChange={(e) => handleTicketSetChange(set.id, 'startSeat', e.target.value)}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>End Seat</label>
                  <input
                    type="text"
                    placeholder="e.g., 4"
                    className={styles.formInput}
                    value={set.endSeat}
                    onChange={(e) => handleTicketSetChange(set.id, 'endSeat', e.target.value)}
                  />
                </div>
              </div>

              {!isBulkSeasonSchedule && listPriceRequired && (
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>List Price ($) *</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder="e.g., 150.00"
                      className={styles.formInput}
                      value={set.listPrice}
                      onChange={(e) => handleTicketSetChange(set.id, 'listPrice', sanitizePriceInput(e.target.value))}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}

        <button
          className={styles.addTicketSetBtn}
          onClick={handleAddTicketSet}
          type="button"
        >
          <AddIcon fontSize="small" />
          Add Another Ticket Set
        </button>
      </div>
    );
  };

  // ==================== RENDER STEP CONTENT ====================
  const renderStepContent = () => {
    const priceOk = !listPriceRequired || (!isBulkSeasonSchedule
      ? ticketSets.every(set => !!set.listPrice.trim() && parseFloat(set.listPrice) > 0)
      : selectedBulkEvents.length > 0 && selectedBulkEvents.every((event) => isBulkEventPriceValid(event.e_id))
    );

    if (selectedLeague?.l_type === 'Sports') {
      switch(currentStep) {
        case 1:
          return (
            <div className={styles.stepContent}>
              <div className={styles.userSelectionSection}>
                <h3 className={styles.sectionTitle}>
                  <FaUser size={17} style={{ marginRight: '6px' }} /> Select User
                </h3>
                
                {showUserResults && (
                  <div className={styles.userResults}>
                    {usersLoading && <div className={styles.loading}>Loading users...</div>}
                    {usersError && <div className={styles.error}>Error loading users</div>}
                    {!usersLoading && !usersError && filteredUsers.length > 0 ? (
                      filteredUsers.map((user: User) => (
                        <div 
                          key={user.u_id}
                          className={styles.userResultItem}
                          onClick={() => handleUserSelect(user)}
                        >
                          <div className={styles.userResultInfo}>
                            <span className={styles.userResultName}>
                              {user.u_full_name || `${user.u_first_name} ${user.u_last_name}`}
                            </span>
                            <span className={styles.userResultEmail}>{user.u_email_id}</span>
                          </div>
                        </div>
                      ))
                    ) : !usersLoading && !usersError && (
                      <div className={styles.noResults}>No users found</div>
                    )}
                  </div>
                )}

                {selectedUser && !showUserResults && (
                  <div className={styles.selectedUserDisplay}>
                    <div className={styles.selectedUserInfo}>
                      <span className={styles.selectedUserName}>
                        {selectedUser.u_full_name || `${selectedUser.u_first_name} ${selectedUser.u_last_name}`}
                      </span>
                       <span >
                        {getPricingModeLabel(selectedUser.u_pricing_mode)}
                      </span>
                    </div>
                    <CheckCircleIcon className={styles.selectedIcon} />
                  </div>
                )}
              </div>
            </div>
          );

        case 2:
          return (
            <div className={styles.stepContent}>
              <div className={styles.leagueSection}>
                <h3 className={styles.sectionTitle}>
                  <FaTag size={17} style={{ marginRight: '6px' }} /> Select League
                </h3>
                
                {leaguesLoading ? (
                  <div className={styles.loading}>Loading leagues...</div>
                ) : leaguesError ? (
                  <div className={styles.error}>Error loading leagues</div>
                ) : (
                  <>
                    <div className={styles.leagueCategory}>
                      <h4 className={styles.categoryTitle}>Team-Based Sports</h4>
                      <div className={styles.leagueGrid}>
                        {leaguesData?.leagues
                          ?.filter((league: League) => league.l_type === 'Sports')
                          .map((league: League) => (
                            <div
                              key={league.l_id}
                              className={`${styles.leagueCard} ${selectedLeague?.l_id === league.l_id ? styles.selectedLeagueCard : ''}`}
                              onClick={() => handleLeagueSelect(league)}
                            >
                              <div className={styles.leagueCode}>{league.l_name.substring(0, 4)}</div>
                              <div className={styles.leagueName}>{league.l_name}</div>
                              {selectedLeague?.l_id === league.l_id && (
                                <CheckCircleIcon className={styles.selectedIconSmall} />
                              )}
                            </div>
                          ))}
                      </div>
                    </div>
                    
                    <div className={styles.leagueCategory}>
                      <h4 className={styles.categoryTitle}>Non-Team Events</h4>
                      <div className={styles.eventsGrid}>
                        {leaguesData?.leagues
                          ?.filter((league: League) => league.l_type !== 'Sports')
                          .map((league: League) => (
                            <div
                              key={league.l_id}
                              className={`${styles.eventCard} ${selectedLeague?.l_id === league.l_id ? styles.selectedEventCard : ''}`}
                              onClick={() => handleLeagueSelect(league)}
                            >
                              <div className={styles.eventName}>{league.l_name}</div>
                              <div className={styles.eventDescription}>{league.l_name}</div>
                              {selectedLeague?.l_id === league.l_id && (
                                <CheckCircleIcon className={styles.selectedIconSmall} />
                              )}
                            </div>
                          ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          );

        case 3:
          const homeTeamOptions = searchHomeTeam.trim().length === 0
            ? (teamsData?.league_categories || [])
            : homeTeamSearchResults;
          return (
            <div className={styles.stepContent}>
              <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                {selectedUser && (
                  <div className={`${styles.selectedChip} ${styles.chipNeutral}`}>
                    <span style={{ marginRight: 8 }}>{selectedUser.u_full_name || `${selectedUser.u_first_name} ${selectedUser.u_last_name}`}</span>
                    <span className={`${styles.pricingBadge} ${styles.chipSuccess}`}>{getPricingModeLabel(selectedUser.u_pricing_mode)}</span>
                  </div>
                )}
                {selectedLeague && (
                  <div className={`${styles.selectedChip} ${styles.chipNeutral}`}>
                    <span style={{ marginRight: 8 }}>{selectedLeague.l_name}</span>
                  </div>
                )}
                {selectedHomeTeam && (
                  <div className={`${styles.selectedChip} ${styles.chipInfo}`}>
                    <span style={{ marginRight: 8 }}>{selectedHomeTeam.lc_name}</span>
                    <span className={`${styles.pricingBadge} ${styles.chipNeutral}`}>{selectedHomeTeam.lc_market_name}</span>
                  </div>
                )}
                {selectedAwayTeam && (
                  <div className={`${styles.selectedChip} ${styles.chipDanger}`}>
                    <span style={{ marginRight: 8 }}>{selectedAwayTeam.lc_name}</span>
                    <span className={`${styles.pricingBadge} ${styles.chipNeutral}`}>{selectedAwayTeam.lc_market_name}</span>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 12,  display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ display: 'flex', alignItems: 'center', marginRight: 6 }}>
                      <MdOutlineFolder size={12} />
                    </span>
                    Select Home Team
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}>
                    <input
                      type="checkbox"
                      style={{ marginRight: 6 }}
                      checked={isBulkSeasonSchedule}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setIsBulkSeasonSchedule(checked);
                        setSelectedEvent(null);
                        setSelectedBulkEvents([]);
                        setSearchEvent("");
                        if (checked) {
                          setSelectedAwayTeam(null);
                          setSearchAwayTeam("");
                          setStepCompleted(prev => ({ ...prev, 4: true }));
                        } else {
                          setStepCompleted(prev => ({ ...prev, 4: false, 5: false }));
                          if (currentStep >= 5) setCurrentStep(4);
                        }
                      }}
                    /> Bulk (Season Schedule)
                  </label>
                </div>
              </div>

              <div style={{ margin: '16px 0 18px 0', position: 'relative' }}>
                <input
                  type="text"
                  className={styles.dropdownSearchInput}
                  style={{ width: '100%', fontSize: 16, padding: '12px 16px', borderRadius: 10, outline: 'none', boxShadow: 'none' }}
                  placeholder="Search home team..."
                  value={searchHomeTeam}
                  onChange={handleHomeTeamSearchChange}
                  onFocus={() => setShowHomeTeamResults(true)}
                  autoFocus
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxHeight: 350, overflowY: 'auto', marginTop: 4 }}>
                {homeTeamOptions.length > 0 ? (
                  homeTeamOptions.map((team: Team) => (
                    <div
                      key={team.lc_id}
                      onClick={() => handleHomeTeamSelect(team)}
                      className={`${styles.teamCard} ${selectedHomeTeam?.lc_id === team.lc_id ? styles.teamCardSelected : ''}`}
                    >
                      <div className={styles.teamCardTitle}>{team.lc_name}</div>
                      <div className={styles.teamCardSub}>{team.lc_market_name || ''}</div>
                      {team.lc_market_name && (
                        <div className={styles.teamCardMeta}>{team.lc_market_name}</div>
                      )}
                      {selectedHomeTeam?.lc_id === team.lc_id && (
                        <span className={styles.teamCardCheck}>✓</span>
                      )}
                    </div>
                  ))
                ) : (
                  <div style={{ color: '#b0b8c9', fontSize: 15, textAlign: 'center', padding: '30px 0' }}>No teams found</div>
                )}
              </div>
            </div>
          );

        case 4:
          const awayTeamOptions = searchAwayTeam.trim().length === 0
            ? (teamsData?.league_categories || [])
            : awayTeamSearchResults;
          return (
            <div className={styles.stepContent}>
              <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                {selectedUser && (
                  <div className={`${styles.selectedChip} ${styles.chipNeutral}`}>
                    <span style={{ marginRight: 8 }}>{selectedUser.u_full_name || `${selectedUser.u_first_name} ${selectedUser.u_last_name}`}</span>
                    <span className={`${styles.pricingBadge} ${styles.chipSuccess}`}>{getPricingModeLabel(selectedUser.u_pricing_mode)}</span>
                  </div>
                )}
                {selectedLeague && (
                  <div className={`${styles.selectedChip} ${styles.chipNeutral}`}>
                    <span style={{ marginRight: 8 }}>{selectedLeague.l_name}</span>
                  </div>
                )}
                {selectedHomeTeam && (
                  <div className={`${styles.selectedChip} ${styles.chipInfo}`}>
                    <span style={{ marginRight: 8 }}>{selectedHomeTeam.lc_name}</span>
                    <span className={`${styles.pricingBadge} ${styles.chipNeutral}`}>{selectedHomeTeam.lc_market_name}</span>
                  </div>
                )}
                {selectedAwayTeam && (
                  <div className={`${styles.selectedChip} ${styles.chipDanger}`}>
                    <span style={{ marginRight: 8 }}>{selectedAwayTeam.lc_name}</span>
                    <span className={`${styles.pricingBadge} ${styles.chipNeutral}`}>{selectedAwayTeam.lc_market_name}</span>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ display: 'flex', alignItems: 'center', marginRight: 6 }}>
                      <MdOutlineFolder size={12} />
                    </span>
                    Select Away Team
                  </div>
                </div>
              </div>

              <div style={{ margin: '16px 0 18px 0', position: 'relative' }}>
                <input
                  type="text"
                  className={styles.dropdownSearchInput}
                  style={{ width: '100%', fontSize: 16, padding: '12px 16px', borderRadius: 10, color: '#2a3b5a', outline: 'none', boxShadow: 'none' }}
                  placeholder="Search away team..."
                  value={searchAwayTeam}
                  onChange={handleAwayTeamSearchChange}
                  onFocus={() => setShowAwayTeamResults(true)}
                  autoFocus
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxHeight: 350, overflowY: 'auto', marginTop: 4 }}>
                {awayTeamOptions.length > 0 ? (
                  awayTeamOptions.map((team: Team) => (
                    <div
                      key={team.lc_id}
                      onClick={() => handleAwayTeamSelect(team)}
                      className={`${styles.teamCard} ${selectedAwayTeam?.lc_id === team.lc_id ? styles.teamCardSelected : ''}`}
                    >
                      <div className={styles.teamCardTitle}>{team.lc_name}</div>
                      <div className={styles.teamCardSub}>{team.lc_market_name || ''}</div>
                      {selectedAwayTeam?.lc_id === team.lc_id && (
                        <span className={styles.teamCardCheck}>✓</span>
                      )}
                    </div>
                  ))
                ) : (
                  <div style={{ color: '#b0b8c9', fontSize: 15, textAlign: 'center', padding: '30px 0' }}>No teams found</div>
                )}
              </div>
            </div>
          );

        case 5:
          return (
            <div className={styles.stepContent}>
              <div className={styles.detailsSection}>
                <h3 className={styles.sectionTitle}>Ticket Details</h3>
                
                <div className={styles.gameInfo}>
                  <div className={styles.gameInfoHeader}>MATCH</div>
                  <div className={styles.gameInfoValue}>
                    {isBulkSeasonSchedule
                      ? selectedHomeTeam?.lc_name
                      : `${selectedHomeTeam?.lc_name} vs ${selectedAwayTeam?.lc_name}`}
                  </div>
                  
                  <div className={styles.gameInfoHeader}>VENUE</div>
                  <div className={styles.gameInfoValue}>
                    {selectedHomeTeam?.lc_market_name || 'TBD'}
                  </div>
                  
                  <div className={styles.gameInfoHeader}>DATE & TIME</div>
                  <div className={styles.gameInfoValue}>
                    {selectedEvent ? formatEventDateTime(selectedEvent) : 'Select event below'}
                  </div>
                  {isBulkSeasonSchedule && (
                    <>
                      <div className={styles.gameInfoHeader}>SELECTED EVENTS</div>
                      <div className={styles.gameInfoValue}>{selectedBulkEvents.length}</div>
                    </>
                  )}
                  
                  <div className={styles.gameInfoHeader}>LEAGUE</div>
                  <div className={styles.gameInfoValue}>{selectedLeague?.l_name || ''}</div>
                </div>

                <div className={styles.eventsList}>
                  <h4 className={styles.eventsListTitle}>Available Events for {selectedLeague?.l_name}</h4>
                  <br></br>
                  
                  <div className={styles.searchWrapper} style={{ marginBottom: '20px' }}>
                    <SearchIcon className={styles.searchIcon} />
                    <input
                      type="text"
                      placeholder="Search events by name, venue, or location..."
                      className={styles.searchInput}
                      value={searchEvent}
                      onChange={handleEventSearchChange}
                    />
                  </div>

                  {eventsLoading && (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                      <CircularProgress size={30} />
                      <p>Loading events...</p>
                    </div>
                  )}
                  
                  {eventsError && !eventsLoading && (
                    <div style={{ textAlign: 'center', padding: '20px', color: 'red' }}>
                      <p>Error loading events. Please refresh.</p>
                    </div>
                  )}
                  
                  {!eventsLoading && !eventsError && filteredEvents.length > 0 ? (
                    <div className={styles.eventsScrollContainer}>
                      <div className={styles.eventsGrid}>
                        {filteredEvents.map((event: Event) => (
                          (() => {
                            const isEventSelected = isBulkSeasonSchedule
                              ? selectedBulkEvents.some((selected) => selected.e_id === event.e_id)
                              : selectedEvent?.e_id === event.e_id;
                            return (
                          <div
                            key={event.e_id}
                            className={`${styles.eventCard} ${isEventSelected ? styles.selectedEventCard : ''}`}
                            onClick={() => handleEventSelect(event)}
                          >
                            <div className={styles.eventName}>{event.e_name}</div>
                            <div className={styles.eventDate}>
                              {formatEventDateTime(event)}
                            </div>
                            <div className={styles.eventVenue}>{event.e_stadium_name || 'Venue TBD'}</div>
                            <div className={styles.eventLocation}>
                              {[event.e_city, event.e_state].filter(Boolean).join(', ') || 'Location TBD'}
                            </div>
                            {isEventSelected && (
                              <CheckCircleIcon className={styles.selectedIconSmall} />
                            )}
                          </div>
                            );
                          })()
                        ))}
                      </div>
                    </div>
                  ) : !eventsLoading && !eventsError && (
                    <div className={styles.noResults}>No events available for {selectedLeague?.l_name}</div>
                  )}
                </div>
                <br></br>
                {(!eventsLoading && !eventsError && filteredEvents.length > 0) && (
                  <>
                    {/* Render Ticket Sets Form */}
                    {renderTicketSetsForm()}

                    {isBulkSeasonSchedule && (
                      <div className={styles.selectedEventsSection}>
                        <div className={styles.selectedEventsHeader}>
                          <h4 className={styles.selectedEventsTitle}>Selected Events</h4>
                          <span className={styles.selectedEventsCount}>{selectedBulkEvents.length} selected</span>
                        </div>
                        <div className={styles.selectedEventsGrid}>
                          {selectedBulkEvents.map((event: Event) => {
                            const priceValue = getBulkEventPrice(event.e_id);
                            const invalidPrice = listPriceRequired && !isBulkEventPriceValid(event.e_id);
                            return (
                              <div key={event.e_id} className={styles.selectedEventCard}>
                                <div className={styles.selectedEventMeta}>
                                  <div className={styles.selectedEventName}>{event.e_name}</div>
                                  <div className={styles.selectedEventSubtitle}>{event.e_stadium_name || 'Venue TBD'}</div>
                                  <div className={styles.selectedEventSubtitle}>{event.e_date ? new Date(event.e_date).toLocaleString('en-US', {
                                    weekday: 'short',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: 'numeric',
                                    minute: '2-digit'
                                  }) : 'Date TBD'}</div>
                                </div>

                                {listPriceRequired ? (
                                  <div className={styles.selectedEventPriceRow}>
                                    <label className={styles.formLabel}>Price per Ticket *</label>
                                    <input
                                      type="text"
                                      inputMode="decimal"
                                      className={`${styles.formInput} ${invalidPrice ? styles.inputError : ''}`}
                                      value={priceValue}
                                      onChange={(e) => setBulkEventPrices((prev) => ({ ...prev, [event.e_id]: sanitizePriceInput(e.target.value) }))}
                                      placeholder="e.g. 120.00"
                                    />
                                    {invalidPrice && (
                                      <div className={styles.eventPriceError}>Please enter a valid price for this event.</div>
                                    )}
                                  </div>
                                ) : (
                                  <div className={styles.conciergeNote}>
                                    Concierge pricing will be set after listing.
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <div className={styles.verificationNote}>
                      <FaInfoCircle size={14} style={{ marginRight: '4px' }} /> Tickets will be listed after verification
                    </div>

                  </>
                )}

                <button 
                  className={`${styles.transferButton} ${
                    !ticketSets.every(set => set.section && set.row && set.startSeat) || 
                    !priceOk || 
                    (isBulkSeasonSchedule ? selectedBulkEvents.length === 0 : !selectedEvent) ? 
                    styles.disabledButton : ''
                  }`}
                  onClick={handleTransferTicketClick}
                  disabled={
                    !ticketSets.every(set => set.section && set.row && set.startSeat) || 
                    !priceOk || 
                    (isBulkSeasonSchedule ? selectedBulkEvents.length === 0 : !selectedEvent)
                  }
                >
                  {isSubmitting ? (
                    <>
                      <CircularProgress size={20} color="inherit" style={{ marginRight: '8px' }} />
                      Adding...
                    </>
                  ) : (
                    <>
                      Transfer Ticket
                      <ArrowForwardIcon className={styles.transferIcon} />
                    </>
                  )}
                </button>
                
                {(!ticketSets.every(set => set.section && set.row && set.startSeat) || 
                  !priceOk || 
                  (isBulkSeasonSchedule ? selectedBulkEvents.length === 0 : !selectedEvent)) && (
                  <p className={styles.warningText}>
                    {
                      (isBulkSeasonSchedule ? selectedBulkEvents.length === 0 : !selectedEvent) ? 
                        (isBulkSeasonSchedule ? 'Please select at least one event' : 'Please select an event') :
                      !ticketSets.every(set => set.section) ? 'Please enter section for all ticket sets' :
                      !ticketSets.every(set => set.row) ? 'Please enter row for all ticket sets' :
                      !ticketSets.every(set => set.startSeat) ? 'Please enter start seat for all ticket sets' :
                      listPriceRequired && !priceOk ? 'Please enter list price for all ticket sets' : ''}
                  </p>
                )}
              </div>
            </div>
          );

        default:
          return null;
      }
    } else {
      switch(currentStep) {
        case 1:
          return (
            <div className={styles.stepContent}>
              <div className={styles.userSelectionSection}>
                <h3 className={styles.sectionTitle}>
                  <FaUser size={17} style={{ marginRight: '6px' }} /> Select User
                </h3>
                
                <div className={styles.searchWrapper}>
                  <SearchIcon className={styles.searchIcon} />
                  <input 
                    type="text"
                    placeholder="Search by name or email..."
                    className={styles.searchInput}
                    value={searchUser}
                    onChange={handleUserSearchChange}
                    onFocus={() => searchUser.length > 0 && setShowUserResults(true)}
                    autoFocus
                  />
                </div>

                {showUserResults && (
                  <div className={styles.userResults}>
                    {usersLoading && <div className={styles.loading}>Loading users...</div>}
                    {usersError && <div className={styles.error}>Error loading users</div>}
                    {!usersLoading && !usersError && filteredUsers.length > 0 ? (
                      filteredUsers.map((user: User) => (
                        <div 
                          key={user.u_id}
                          className={styles.userResultItem}
                          onClick={() => handleUserSelect(user)}
                        >
                          <div className={styles.userResultInfo}>
                            <span className={styles.userResultName}>
                              {user.u_full_name || `${user.u_first_name} ${user.u_last_name}`}
                            </span>
                            <span className={styles.userResultEmail}>{user.u_email_id}</span>
                          </div>
                        </div>
                      ))
                    ) : !usersLoading && !usersError && (
                      <div className={styles.noResults}>No users found</div>
                    )}
                  </div>
                )}

                {selectedUser && !showUserResults && (
                  <div className={styles.selectedUserDisplay}>
                    <div className={styles.selectedUserInfo}>
                      <span className={styles.selectedUserName}>
                        {selectedUser.u_full_name || `${selectedUser.u_first_name} ${selectedUser.u_last_name}`}
                      </span>
                    </div>
                    <CheckCircleIcon className={styles.selectedIcon} />
                  </div>
                )}
              </div>
            </div>
          );

        case 2:
          return (
            <div className={styles.stepContent}>
              <div className={styles.leagueSection}>
                <h3 className={styles.sectionTitle}>
                  <FaTag size={12} style={{ marginRight: '6px' }} /> Select League
                </h3>
                
                {leaguesLoading ? (
                  <div className={styles.loading}>Loading leagues...</div>
                ) : leaguesError ? (
                  <div className={styles.error}>Error loading leagues</div>
                ) : (
                  <>
                    <div className={styles.leagueCategory}>
                      <h4 className={styles.categoryTitle}>Team-Based Sports</h4>
                      <div className={styles.leagueGrid}>
                        {leaguesData?.leagues
                          ?.filter((league: League) => league.l_type === 'Sports')
                          .map((league: League) => (
                            <div
                              key={league.l_id}
                              className={`${styles.leagueCard} ${selectedLeague?.l_id === league.l_id ? styles.selectedLeagueCard : ''}`}
                              onClick={() => handleLeagueSelect(league)}
                            >
                              <div className={styles.leagueCode}>{league.l_name.substring(0, 4)}</div>
                              <div className={styles.leagueName}>{league.l_name}</div>
                              {selectedLeague?.l_id === league.l_id && (
                                <CheckCircleIcon className={styles.selectedIconSmall} />
                              )}
                            </div>
                          ))}
                      </div>
                    </div>
                    
                    <div className={styles.leagueCategory}>
                      <h4 className={styles.categoryTitle}>Non-Team Events</h4>
                      <div className={styles.eventsGrid}>
                        {leaguesData?.leagues
                          ?.filter((league: League) => league.l_type !== 'Sports')
                          .map((league: League) => (
                            <div
                              key={league.l_id}
                              className={`${styles.eventCard} ${selectedLeague?.l_id === league.l_id ? styles.selectedEventCard : ''}`}
                              onClick={() => handleLeagueSelect(league)}
                            >
                              <div className={styles.eventName}>{league.l_name}</div>
                              <div className={styles.eventDescription}>{league.l_name}</div>
                              {selectedLeague?.l_id === league.l_id && (
                                <CheckCircleIcon className={styles.selectedIconSmall} />
                              )}
                            </div>
                          ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          );

        case 3:
          return (
            <div className={styles.stepContent}>
              <div className={styles.eventSection}>
                <h3 className={styles.sectionTitle}>Search Event for {selectedLeague?.l_name}</h3>
                
                <div className={styles.searchWrapper}>
                  <SearchIcon className={styles.searchIcon} />
                  <input 
                    type="text"
                    placeholder="Search by event name..."
                    className={styles.searchInput}
                    value={searchEvent}
                    onChange={handleEventSearchChange}
                    onFocus={() => setShowEventResults(true)}
                    autoFocus
                  />
                </div>

                {showEventResults && (
                  <div className={styles.eventResults}>
                    {eventsLoading && (
                      <div style={{ textAlign: 'center', padding: '20px' }}>
                        <CircularProgress size={30} />
                        <p>Loading events...</p>
                      </div>
                    )}
                    
                    {eventsError && !eventsLoading && (
                      <div style={{ textAlign: 'center', padding: '20px', color: 'red' }}>
                        <p>Error loading events. Please refresh.</p>
                      </div>
                    )}
                    
                    {!eventsLoading && !eventsError && filteredEvents.length > 0 ? (
                      filteredEvents.map((event: Event) => {
                        const eventDate = event.e_date ? new Date(event.e_date) : null;
                        const dateLabel = eventDate
                          ? `${eventDate.getFullYear()}-${String(eventDate.getMonth() + 1).padStart(2, '0')}-${String(eventDate.getDate()).padStart(2, '0')}`
                          : 'Date TBD';
                        const venueLabel = event.e_stadium_name || event.e_address || 'Venue TBD';
                        const locationLabel = [event.e_city, event.e_state].filter(Boolean).join(', ') || 'Location TBD';

                        return (
                          <div
                            key={event.e_id}
                            className={`${styles.eventResultItem} ${selectedEvent?.e_id === event.e_id ? styles.selectedEventResultItem : ''}`}
                            onClick={() => handleEventSelect(event)}
                          >
                            <div className={styles.eventResultLeft}>
                              <div className={styles.eventIconWrap}>
                                <FaCalendarAlt size={16} />
                              </div>
                              <div className={styles.eventResultMeta}>
                                <span className={styles.eventResultName}>{event.e_name}</span>
                                <span className={styles.eventResultVenue}>{venueLabel}</span>
                              </div>
                            </div>

                            <div className={styles.eventResultRight}>
                              <span className={styles.eventResultDate}>{dateLabel}</span>
                              <span className={styles.eventResultLocation}>{locationLabel}</span>
                            </div>
                          </div>
                        );
                      })
                    ) : !eventsLoading && !eventsError && (
                      <div className={styles.noResults}>No events found for {selectedLeague?.l_name}</div>
                    )}
                  </div>
                )}

                {selectedEvent && !showEventResults && (
                  <div className={styles.selectedEventDisplay}>
                    <div className={styles.selectedEventInfo}>
                      <span className={styles.selectedEventName}>{selectedEvent.e_name}</span>
                      <span className={styles.selectedEventDate}>
                        {formatEventDateTime(selectedEvent)}
                      </span>
                      <span className={styles.selectedEventVenue}>{selectedEvent.e_stadium_name}</span>
                      <span className={styles.selectedEventLocation}>{selectedEvent.e_city}, {selectedEvent.e_state}</span>
                    </div>
                    <CheckCircleIcon className={styles.selectedIcon} />
                  </div>
                )}
              </div>
            </div>
          );

        case 4:
          return (
            <div className={styles.stepContent}>
              <div className={styles.detailsSection}>
                <h3 className={styles.sectionTitle}>Ticket Details</h3>
                
                <div className={styles.gameInfo}>
                  <div className={styles.gameInfoHeader}>EVENT</div>
                  <div className={styles.gameInfoValue}>
                    {selectedEvent?.e_name}
                  </div>
                  
                  <div className={styles.gameInfoHeader}>VENUE</div>
                  <div className={styles.gameInfoValue}>
                    {selectedEvent?.e_stadium_name || 'TBD'}
                  </div>
                  
                  <div className={styles.gameInfoHeader}>DATE & TIME</div>
                  <div className={styles.gameInfoValue}>
                    {selectedEvent?.e_date 
                      ? new Date(selectedEvent.e_date).toLocaleString('en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit'
                        })
                      : 'TBD'
                    }
                  </div>
                  
                  <div className={styles.gameInfoHeader}>LEAGUE</div>
                  <div className={styles.gameInfoValue}>{selectedLeague?.l_name || ''}</div>
                </div>

                {/* Render Ticket Sets Form for non-sports events */}
                {renderTicketSetsForm()}

                <div className={styles.verificationNote}>
                  <FaInfoCircle size={14} style={{ marginRight: '4px' }} /> Tickets will be listed after verification
                </div>

                <div className={styles.totalPriceSummary}>
                  <span>Total Value: </span>
                  <strong>${getTotalPrice().toFixed(2)}</strong>
                </div>

                <button 
                  className={`${styles.transferButton} ${
                    !ticketSets.every(set => set.section && set.row && set.startSeat) || 
                    !priceOk || 
                    !selectedEvent ? 
                    styles.disabledButton : ''
                  }`}
                  onClick={handleTransferTicketClick}
                  disabled={
                    !ticketSets.every(set => set.section && set.row && set.startSeat) || 
                    !priceOk || 
                    !selectedEvent
                  }
                >
                  {isSubmitting ? (
                    <>
                      <CircularProgress size={20} color="inherit" style={{ marginRight: '8px' }} />
                      Adding...
                    </>
                  ) : (
                    <>
                      Transfer Ticket
                      <ArrowForwardIcon className={styles.transferIcon} />
                    </>
                  )}
                </button>
                
                {(!ticketSets.every(set => set.section && set.row && set.startSeat) || 
                  !priceOk || 
                  !selectedEvent) && (
                  <p className={styles.warningText}>
                    {
                      !selectedEvent ? 'Please select an event' :
                      !ticketSets.every(set => set.section) ? 'Please enter section for all ticket sets' :
                      !ticketSets.every(set => set.row) ? 'Please enter row for all ticket sets' :
                      !ticketSets.every(set => set.startSeat) ? 'Please enter start seat for all ticket sets' :
                      listPriceRequired && !priceOk ? 'Please enter list price for all ticket sets' : ''}
                  </p>
                )}
              </div>
            </div>
          );

        default:
          return null;
      }
    }
  };

  return (
    <div className={`${styles.container} ${isDarkMode ? styles.darkMode : ''}`}>
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <div className={styles.header}>
        <div>
          <p className={styles.subtitle}>Transfer New Tickets</p>
          <h1 className={styles.title}>Add Tickets</h1>
        </div>

        <div className={styles.searchSection}>
          <button 
            className={styles.iconBtn} 
            onClick={handleRefresh}
            title="Refresh"
          >
            <RefreshRoundedIcon />
          </button>
          <button 
            className={styles.iconBtn}
            onClick={() => setIsDarkMode(!isDarkMode)}
            title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
          >
            {isDarkMode ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
          </button>
        </div>
      </div>

      <div className={styles.mainContent}>
        <div className={styles.stepperContainer}>
          <div className={styles.stepper}>
            <div 
              className={`${styles.stepItem} ${currentStep === 1 ? styles.activeStepItem : ''} ${stepCompleted[1] ? styles.completedStep : ''}`}
              onClick={() => handleStepClick(1)}
            >
              <span className={styles.stepNumber}>1</span>
              <span className={styles.stepLabel}>User</span>
              {stepCompleted[1] && <CheckCircleIcon className={styles.stepCompletedIcon} />}
            </div>
            <span className={styles.stepSeparator}>{">"}</span>
            
            <div 
              className={`${styles.stepItem} ${currentStep === 2 ? styles.activeStepItem : ''} ${stepCompleted[2] ? styles.completedStep : ''} ${!stepCompleted[1] ? styles.disabledStep : ''}`}
              onClick={() => handleStepClick(2)}
            >
              <span className={styles.stepNumber}>2</span>
              <span className={styles.stepLabel}>League</span>
              {stepCompleted[2] && <CheckCircleIcon className={styles.stepCompletedIcon} />}
            </div>
            
            {selectedLeague?.l_type === 'Sports' ? (
              <>
                <span className={styles.stepSeparator}>{">"}</span>
                
                <div 
                  className={`${styles.stepItem} ${currentStep === 3 ? styles.activeStepItem : ''} ${stepCompleted[3] ? styles.completedStep : ''} ${!stepCompleted[2] ? styles.disabledStep : ''}`}
                  onClick={() => handleStepClick(3)}
                >
                  <span className={styles.stepNumber}>3</span>
                  <span className={styles.stepLabel}>Home Team</span>
                  {stepCompleted[3] && <CheckCircleIcon className={styles.stepCompletedIcon} />}
                </div>
                <span className={styles.stepSeparator}>{">"}</span>
                
                <div 
                  className={`${styles.stepItem} ${currentStep === 4 ? styles.activeStepItem : ''} ${stepCompleted[4] ? styles.completedStep : ''} ${(!stepCompleted[3] || isBulkSeasonSchedule) ? styles.disabledStep : ''}`}
                  onClick={() => handleStepClick(4)}
                >
                  <span className={styles.stepNumber}>4</span>
                  <span className={styles.stepLabel}>Away Team</span>
                  {stepCompleted[4] && <CheckCircleIcon className={styles.stepCompletedIcon} />}
                </div>
                <span className={styles.stepSeparator}>{">"}</span>
                
                <div 
                  className={`${styles.stepItem} ${currentStep === 5 ? styles.activeStepItem : ''} ${!(stepCompleted[4] || isBulkSeasonSchedule) ? styles.disabledStep : ''}`}
                  onClick={() => handleStepClick(5)}
                >
                  <span className={styles.stepNumber}>5</span>
                  <span className={styles.stepLabel}>Details</span>
                </div>
              </>
            ) : (
              <>
                <span className={styles.stepSeparator}>{">"}</span>
                
                <div 
                  className={`${styles.stepItem} ${currentStep === 3 ? styles.activeStepItem : ''} ${stepCompleted[3] ? styles.completedStep : ''} ${!stepCompleted[2] ? styles.disabledStep : ''}`}
                  onClick={() => handleStepClick(3)}
                >
                  <span className={styles.stepNumber}>3</span>
                  <span className={styles.stepLabel}>Search Event</span>
                  {stepCompleted[3] && <CheckCircleIcon className={styles.stepCompletedIcon} />}
                </div>
                <span className={styles.stepSeparator}>{">"}</span>
                
                <div 
                  className={`${styles.stepItem} ${currentStep === 4 ? styles.activeStepItem : ''} ${!stepCompleted[3] ? styles.disabledStep : ''}`}
                  onClick={() => handleStepClick(4)}
                >
                  <span className={styles.stepNumber}>4</span>
                  <span className={styles.stepLabel}>Details</span>
                </div>
              </>
            )}
          </div>
        </div>

        <div className={styles.stepContainer}>
          {renderStepContent()}
        </div>
      </div>

      <Dialog
        open={showConfirmDialog}
        onClose={handleCancel}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="confirm-dialog-title" className={styles.dialogTitle}>
          Confirm Add Tickets
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description" className={styles.dialogContent}>
            Are you sure you want to add these tickets?
          </DialogContentText>
          
          {selectedUser && (selectedEvent || selectedBulkEvents.length > 0) && (
            <div className={styles.confirmationSummary}>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>User:</span>
                <span className={styles.summaryValue}>
                  {selectedUser.u_full_name || `${selectedUser.u_first_name} ${selectedUser.u_last_name}`}
                </span>
              </div>
              {(isBulkSeasonSchedule ? selectedBulkEvents : selectedEvent ? [selectedEvent] : []).map((event) => (
                <div key={event.e_id} className={styles.eventSummaryCard}>
                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Event:</span>
                    <span className={styles.summaryValue}>{event.e_name}</span>
                  </div>
                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Venue:</span>
                    <span className={styles.summaryValue}>{event.e_stadium_name || event.e_address || 'TBD'}</span>
                  </div>
                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Date & Time:</span>
                    <span className={styles.summaryValue}>{formatEventDateTime(event)}</span>
                  </div>
                  
                  {/* Show all ticket sets in confirmation */}
                  {ticketSets.map((set, index) => (
                    <div key={set.id} className={styles.ticketSetSummary}>
                      <div className={styles.ticketSetSummaryTitle}>Ticket Set {index + 1}</div>
                      <div className={styles.summaryItem}>
                        <span className={styles.summaryLabel}>Section:</span>
                        <span className={styles.summaryValue}>{set.section || 'N/A'}</span>
                      </div>
                      <div className={styles.summaryItem}>
                        <span className={styles.summaryLabel}>Row:</span>
                        <span className={styles.summaryValue}>{set.row || 'N/A'}</span>
                      </div>
                      <div className={styles.summaryItem}>
                        <span className={styles.summaryLabel}>Seats:</span>
                        <span className={styles.summaryValue}>{set.startSeat}{set.endSeat ? ` - ${set.endSeat}` : ''}</span>
                      </div>
                      {listPriceRequired && !isBulkSeasonSchedule && (
                        <div className={styles.summaryItem}>
                          <span className={styles.summaryLabel}>Price:</span>
                          <span className={styles.summaryValue}>${parseFloat(set.listPrice || '0').toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  ))}
                  {isBulkSeasonSchedule && listPriceRequired && (
                    <div className={styles.summaryItem}>
                      <span className={styles.summaryLabel}>Price per ticket:</span>
                      <span className={styles.summaryValue}>
                        ${parseFloat(getBulkEventPrice(event.e_id) || '0').toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
              ))}
              {listPriceRequired && !isBulkSeasonSchedule && (
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>Total value:</span>
                  <span className={styles.summaryValueHighlight}>
                    ${getTotalPrice().toFixed(2)}
                  </span>
                </div>
              )}
            </div>
          )}
          
          <DialogContentText className={styles.dialogWarning}>
            This action will store the tickets in the database and they will appear in Manage Tickets.
          </DialogContentText>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button 
            onClick={handleCancel} 
            color="inherit"
            variant="outlined"
            className={styles.cancelButton}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmAdd} 
            color="primary" 
            variant="contained"
            className={styles.confirmButton}
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isSubmitting ? 'Adding...' : 'Add Tickets'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AddTickets;